/**
 * `/fan`, keyed on the search param `accountId`, following the `?poolId=`
 * pattern `pools.tsx` already uses. Search-param plumbing plus three child
 * mounts; no data fetching and no business logic of its own. `?sheet=`
 * opens the actions sheet or, from it, the report sheet (ISSUE-1375).
 */
import {
  createFileRoute,
  useNavigate,
  useSearch,
} from "@tanstack/react-router";
import type { ReactNode } from "react";

import { requireSignedIn } from "../auth/requireSignedIn";
import { FanActionsDialog } from "../features/fanProfile/FanActionsDialog";
import { FAN_PROFILE_HEADING } from "../features/fanProfile/fanProfileCopy";
import { FanProfilePane } from "../features/fanProfile/FanProfilePane";
import { FanReportDialog } from "../features/fanProfile/FanReportDialog";
import { closeOverlay } from "../routeState/overlay";
import { validateFanSearch } from "../routeState/sectionSearch";
import { SectionScreen } from "../shell/SectionScreen";

const FAN_ACTIONS_SHEET = "fan-actions";
const FAN_REPORT_SHEET = "fan-report";

const FanScreen = (): ReactNode => {
  const search = useSearch({ from: "/fan" });
  const navigate = useNavigate({ from: "/fan" });

  return (
    <SectionScreen heading={FAN_PROFILE_HEADING}>
      <FanProfilePane
        accountId={search.accountId}
        onOpenActions={() => {
          void navigate({
            search: (prev) => ({ ...prev, sheet: FAN_ACTIONS_SHEET }),
          });
        }}
      />
      <FanActionsDialog
        open={search.sheet === FAN_ACTIONS_SHEET}
        onClose={() => {
          void navigate({
            search: (prev) => closeOverlay(prev, { kind: "sheet" }),
            replace: true,
          });
        }}
        accountId={search.accountId ?? ""}
        onReport={() => {
          void navigate({
            search: (prev) => ({ ...prev, sheet: FAN_REPORT_SHEET }),
            replace: true,
          });
        }}
      />
      <FanReportDialog
        open={search.sheet === FAN_REPORT_SHEET}
        onClose={() => {
          void navigate({
            search: (prev) => closeOverlay(prev, { kind: "sheet" }),
            replace: true,
          });
        }}
        accountId={search.accountId ?? ""}
      />
    </SectionScreen>
  );
};

export const Route = createFileRoute("/fan")({
  beforeLoad: requireSignedIn,
  validateSearch: validateFanSearch,
  component: FanScreen,
});
